import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import useWindowDimensions from '../../../util/useWindowDimensions.ts';
import { TutorialTooltip } from './tutorial_tooltip.tsx';

export const SpotlightPortalContext = createContext<HTMLElement | null>(null);

type SpotlightOptions = {
  /** Extra space around the target element */
  padding?: number;
  /** Corner radius for the hole */
  radius?: number;
  /** Overlay darkness (0..1) */
  dimOpacity?: number;
  /** Transition duration in ms */
  transitionMs?: number;
  /** If true, only clicking in the highlighted area triggers onDismiss, otherwise clicking anywhere works */
  requireClickInSpotlight?: boolean;
};

type Rect = { x: number; y: number; width: number; height: number; r: number };

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function computeRect(
  el: Element,
  padding: number,
  radius: number,
  isCircular?: boolean,
  container?: HTMLElement | null,
): Rect {
  const b = el.getBoundingClientRect();
  const offset = container?.getBoundingClientRect();
  const offsetX = offset?.left ?? 0;
  const offsetY = offset?.top ?? 0;
  const width = Math.round(b.width + padding * 2);
  const height = Math.round(b.height + padding * 2);
  const length = Math.max(width, height); // square -> circular spotlight
  const centerX = b.left - offsetX + b.width / 2;
  const centerY = b.top - offsetY + b.height / 2;
  const x = Math.round(isCircular ? centerX - length / 2 : b.left - offsetX - padding);
  const y = Math.round(isCircular ? centerY - length / 2 : b.top - offsetY - padding);

  // Corner radius can't exceed half of width/height.
  const r = clamp(radius, 0, length / 2);

  return { x, y, width: isCircular ? length : width, height: isCircular ? length : height, r };
}

function useStableId(prefix = 'spotlight') {
  // React 18 has useId, but this works everywhere.
  const idRef = useRef<string>(null);
  // eslint-disable-next-line
  if (!idRef.current) idRef.current = `${prefix}-${Math.random().toString(36).slice(2)}`;
  return idRef.current;
}

/**
 * Hook that returns a Spotlight component you can render when active.
 *
 * Usage:
 *   const { Spotlight, open, close, isOpen } = useSpotlight({ ... });
 *   <button ref={btnRef} ... />
 *   {isOpen && <Spotlight targetRef={btnRef} onDismiss={close} />}
 */
type SpotlightProps = {
  targetElement: HTMLElement | null;
  onDismiss?: () => void;
  /** Override options per instance */
  options?: SpotlightOptions;
  /** Optional: aria label for accessibility */
  ariaLabel?: string;
  isCircular?: boolean;
};

enum TapMessagePosition {
  Before = 'before',
  Center = 'center',
  After = 'after',
}

export const spotlightTextBorderRadius = 12;

export const Spotlight: React.FC<SpotlightProps> = ({
  targetElement,
  onDismiss,
  options,
  ariaLabel = 'Spotlight overlay',
  isCircular,
}) => {
  const portalContainer = React.use(SpotlightPortalContext);
  const opts = {
    padding: 10,
    radius: isCircular ? 9999 : spotlightTextBorderRadius,
    dimOpacity: 0.5,
    transitionMs: 750,
    requireClickInSpotlight: false,
    ...options,
  };
  const [rect, setRect] = useState<Rect | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [svgHeight, setSvgHeight] = useState(9999);
  const { width: viewportWidth } = useWindowDimensions();

  const maskId = useStableId('mask');
  const overlayId = useStableId('overlay');

  const scheduleUpdate = useCallback(() => {
    // scheduleUpdate is invoked both from a useEffect (flagged by the rule) and from scroll/resize
    // listeners (not flagged). The effect-driven call is required here: we need to measure
    // targetElement after React commits so its layout is stable. Suppressing at the setRect sites.
    if (!targetElement) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setRect(null);
      return;
    }
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setRect(computeRect(targetElement, opts.padding!, opts.radius!, isCircular, portalContainer));
  }, [isCircular, opts.padding, opts.radius, portalContainer, targetElement]);

  useEffect(() => {
     
    scheduleUpdate();
  }, [scheduleUpdate]);

  useEffect(() => {
    const el = targetElement;
    if (!el) return;

    // Keep up with scroll/resize.
    const onScroll = () => scheduleUpdate();
    const onResize = () => scheduleUpdate();

    window.addEventListener('scroll', onScroll, true); // capture to catch nested scrollers
    window.addEventListener('resize', onResize);

    // Watch size changes of the element itself.
    const ro = new ResizeObserver(() => scheduleUpdate());
    ro.observe(el);

    // Also watch DOM/layout changes around it (optional but helpful).
    const mo = new MutationObserver(() => scheduleUpdate());
    mo.observe(document.body, { subtree: true, childList: true, attributes: true });

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      ro.disconnect();
      mo.disconnect();
    };
  }, [scheduleUpdate, targetElement]);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    setSvgHeight(el.getBoundingClientRect().height);
  }, []);

  const containerWidth = portalContainer?.clientWidth ?? viewportWidth;
  const transition = `all ${opts.transitionMs}ms ease`;

  const handleOverlayClick = useCallback(
    (e: any) => {
      if (opts.requireClickInSpotlight && rect) {
        // Click position in viewport coordinates
        const clickX = e.clientX;
        const clickY = e.clientY;

        // Spotlight bounds
        const left = rect.x;
        const right = rect.x + rect.width;
        const top = rect.y;
        const bottom = rect.y + rect.height;

        const insideSpotlight = clickX >= left && clickX <= right && clickY >= top && clickY <= bottom;

        if (!insideSpotlight) {
          // Click was outside the hole → do nothing
          return;
        }
      }
      // Only dismiss when clicking the dimmed region (not the hole).
      // We'll ensure the hole doesn't receive pointer events by placing it in the mask (mask doesn't capture events).
      onDismiss?.();
    },
    [onDismiss, opts.requireClickInSpotlight, rect],
  );

  // When rect is null, render a full dim overlay (no hole).
  const hole = rect ?? { x: -9999, y: -9999, width: 0, height: 0, r: 0 };

  const tapMessageText = opts.requireClickInSpotlight ? 'tap here' : 'tap anywhere';
  const isTapMessageArrow = opts.requireClickInSpotlight;

  // "Tap to continue" is horizontally centered unless it would go off the left edge of the screen
  // It is vertically centered if it wasn't horizontally centered - otherwise it is below unless it wouldn't fit
  let horizontalTapMessagePosition: TapMessagePosition;
  if (isTapMessageArrow) {
    // Favor left/right arrows for "tap here"
    const leftSideOfHole = hole.x;
    const rightSideOfHole = containerWidth - (hole.x + hole.width);
    horizontalTapMessagePosition =
      leftSideOfHole > 100
        ? TapMessagePosition.Before
        : rightSideOfHole > 100
          ? TapMessagePosition.After
          : TapMessagePosition.Center;
  } else {
    const isFarEnoughFromEdgeToBeCentered = hole.x + hole.width / 2 > 40;
    const isTooCloseToEdgeToBeRightArrow = hole.x + hole.width + 15 > containerWidth - 90;
    horizontalTapMessagePosition =
      isFarEnoughFromEdgeToBeCentered || isTooCloseToEdgeToBeRightArrow
        ? TapMessagePosition.Center
        : TapMessagePosition.After;
  }
  const verticalTapMessagePosition =
    horizontalTapMessagePosition === TapMessagePosition.Before ||
    horizontalTapMessagePosition === TapMessagePosition.After
      ? TapMessagePosition.Center
      : hole.y + hole.height + 20 < svgHeight
        ? TapMessagePosition.After
        : TapMessagePosition.Before;

  const tapMessageTop =
    verticalTapMessagePosition === TapMessagePosition.Before
      ? hole.y - 50
      : verticalTapMessagePosition === TapMessagePosition.After
        ? hole.y + hole.height + 15
        : hole.y + hole.height / 2;
  const tapMessageLeft =
    horizontalTapMessagePosition === TapMessagePosition.Center
      ? hole.x + hole.width / 2
      : horizontalTapMessagePosition === TapMessagePosition.After
        ? hole.x + hole.width + 15
        : undefined;
  const tapMessageRight = tapMessageLeft === undefined ? containerWidth - hole.x + 10 : undefined;
  const tapMessageTransform =
    (horizontalTapMessagePosition === TapMessagePosition.Center ? 'translateX(-50%)' : '') +
    (verticalTapMessagePosition === TapMessagePosition.Center ? ' translateY(-50%)' : '');

  const content = (
    <SpotlightWrapper onClick={handleOverlayClick}>
      <svg
        id={overlayId}
        ref={svgRef}
        aria-label={ariaLabel}
        role="dialog"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
        }}
      >
        <defs>
          {/* Mask: white = visible overlay, black = transparent (the hole) */}
          <mask id={maskId}>
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={hole.x}
              y={hole.y}
              width={hole.width}
              height={hole.height}
              rx={hole.r}
              ry={hole.r}
              fill="black"
              style={{ transition }}
            />
          </mask>
        </defs>
        {/* Dimmed overlay (masked) */}
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="var(--color-background-modal-overlay)"
          mask={`url(#${maskId})`}
        />
      </svg>
      <TutorialTooltip
        style={{
          opacity: isTapMessageArrow && horizontalTapMessagePosition === TapMessagePosition.Center ? 1 : 0,
          top: tapMessageTop,
          left: tapMessageLeft,
          right: tapMessageRight,
          transform: tapMessageTransform,
          transition,
        }}
      >
        <TapMessageText>{tapMessageText}</TapMessageText>
      </TutorialTooltip>
      <LeftArrow
        $message={tapMessageText}
        $size={100}
        style={{
          opacity: isTapMessageArrow && horizontalTapMessagePosition === TapMessagePosition.After ? 1 : 0,
          top: tapMessageTop,
          left: tapMessageLeft,
          right: tapMessageRight,
          transform: tapMessageTransform,
          transition,
        }}
      />
      <RightArrow
        $message={tapMessageText}
        $size={100}
        style={{
          opacity: isTapMessageArrow && horizontalTapMessagePosition === TapMessagePosition.Before ? 1 : 0,
          top: tapMessageTop,
          left: tapMessageLeft,
          right: tapMessageRight,
          transform: tapMessageTransform,
          transition,
        }}
      />
      <TapMessage
        style={{
          opacity: !isTapMessageArrow ? 1 : 0,
          top: tapMessageTop,
          left: tapMessageLeft,
          right: tapMessageRight,
          transform: tapMessageTransform,
          transition,
        }}
      >
        {tapMessageText}
      </TapMessage>
    </SpotlightWrapper>
  );
  return createPortal(content, portalContainer ?? document.body);
};

const TapMessageText = styled.div`
  padding: 6px 15px;
  color: var(--color-background);
`;

const SpotlightWrapper = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: 'auto';
`;

const TapMessage = styled.div`
  position: absolute;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 1rem;
  width: max-content;
  color: var(--color-background);
  background: var(--color-text);
  border-radius: 10px;
  padding: 6px 12px;
`;

interface ArrowProps {
  $size: number; // controls overall scale
  $message: string;
  $color?: string;
}

export const LeftArrow = styled.div<ArrowProps>`
  position: absolute;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;

  /* shaft */
  &::after {
    color: var(--color-background);
    content: '${({ $message }) => $message}';
    position: absolute;
    top: 50%;
    left: ${({ $size }) => $size * 0.25}px;
    width: ${({ $size }) => $size * 0.65}px;
    height: ${({ $size }) => $size * 0.25}px;
    background: ${({ $color = 'var(--color-text)' }) => $color};
    transform: translateY(-50%);
  }

  /* arrow head */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    border-top: ${({ $size }) => $size * 0.25}px solid transparent;
    border-bottom: ${({ $size }) => $size * 0.25}px solid transparent;
    border-right: ${({ $size }) => $size * 0.35}px solid ${({ $color = 'var(--color-text)' }) => $color};
    transform: translateY(-50%);
  }
`;

export const RightArrow = styled.div<ArrowProps>`
  position: absolute;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;

  /* shaft */
  &::after {
    color: var(--color-background);
    content: '${({ $message }) => $message}';
    position: absolute;
    top: 50%;
    text-align: right;
    right: ${({ $size }) => $size * 0.25}px;
    width: ${({ $size }) => $size * 0.65}px;
    height: ${({ $size }) => $size * 0.25}px;
    background: ${({ $color = 'var(--color-text)' }) => $color};
    transform: translateY(-50%);
  }

  /* arrow head */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    right: 0;
    border-top: ${({ $size }) => $size * 0.25}px solid transparent;
    border-bottom: ${({ $size }) => $size * 0.25}px solid transparent;
    border-left: ${({ $size }) => $size * 0.35}px solid ${({ $color = 'var(--color-text)' }) => $color};
    transform: translateY(-50%);
  }
`;
